import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, NgZone, OnChanges, Output, signal, SimpleChanges } from '@angular/core';
import { getPhysicalContextLabel } from '../../../../../../core/constants/featureLabels';
import { StakeholdersService } from 'src/app/core/service/api/stakeholders.service';
import { ContextService } from 'src/app/core/service/api/context.service';
import { firstValueFrom } from 'rxjs';
import { showFeedbackError, showFeedbackSaved } from 'src/app/core/util/notification';
import isEqual from 'lodash/isEqual';
import { SubprojectQuery } from 'src/app/akita/subproject/state/subprojects.query';

interface ContextItemRelation {
  contextItem: any;
  stakeholders: any[];
}

@Component({
  selector: 'app-context-sidebar-panel',
  templateUrl: './context-sidebar-panel.component.html',
  styleUrl: './context-sidebar-panel.component.sass',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContextSidebarPanelComponent implements OnChanges {
  @Output() selectedContextItem: EventEmitter<any> = new EventEmitter<any>();
  @Output() onPanelClose: EventEmitter<void> = new EventEmitter<void>();
  @Output() contextTypeChanged: EventEmitter<string> = new EventEmitter<string>();
  @Output() onHoverContext: EventEmitter<{type: string, item: any, is_subtype: boolean}> = new EventEmitter<{type: string, item: any, is_subtype: boolean}>();
  @Output() onLeaveContext: EventEmitter<void> = new EventEmitter<void>();
  @Output() onItemCollapse: EventEmitter<void> = new EventEmitter<void>();
  @Input() ContextanalysisData: any = {};
  @Input() physicalContextData: any = {};
  @Input() totalItems: number = 0;
  expandedItem = signal<{ item: any; type: string } | null>(null);
  isProcessing = signal(false);
  processingType = signal<string | null>(null);
  createdContextTypes = signal<Set<string>>(new Set());
  isSharedSubproject = signal<boolean>(false);
  activeChipId: number = 0;
  private scrollTimeout: any;
  transformedStakeholders: any[] = []; // New array for transformed stakeholders
  
  // Method to get physical context label for template
  getPhysicalLabel(key: string): string {
    return getPhysicalContextLabel(key);
  }
  contextTypes = [];
  contextChipsTypes: any[] = [
    { id: 0, name: 'Physical',   icon: 'apps', count: 15, expanded: true },
    { id: 1, name: 'Regulatory', icon: 'calendar', count: 56, expanded: true },
    { id: 2, name: 'Political',  icon: 'sign-alt', count: 89, expanded: true },
    { id: 3, name: 'Social',     icon: 'stop-circle', count: 19, expanded: true },
    { id: 4, name: 'Emotional',  icon: 'smile', count: 10, expanded: true }
  ];
  perfomanceMetrics: any ={
    subjectTitle: 'Subject title',
    icon: '♀',
    name: 'Name Lorem',
    description: 'ipsum dolor',
    progress: 90
  };
  
  constructor(private _stakeholdersService: StakeholdersService, private subProjectQuery: SubprojectQuery, private _contextsService: ContextService, private cdr: ChangeDetectorRef, private ngZone: NgZone) { }

  ngOnChanges(changes: SimpleChanges): void {
    // If physicalContextData changes and we're on Stakeholders tab, retransform
    if (changes['physicalContextData'] && !changes['physicalContextData'].firstChange) {
      // Only transform if we're currently on Stakeholders tab (index 1)
      // We'll transform on tab change, so this is optional
    }
  }

  ngOnInit(): void {
    const activeSubProject = this.subProjectQuery.getActive();
    this.isSharedSubproject.set(activeSubProject && activeSubProject?.tenant_type === 'shared');
    this._contextsService.getContextTypes().subscribe((res)=>{
      this.contextTypes = res;
    })
  }

  onChipClick(chipId: number): void {
    this.activeChipId = chipId;
    this.scrollToAccordion(chipId);
    
    // Emit the context type that was clicked
    const contextType = this.contextChipsTypes.find(chip => chip.id === chipId)?.name || 'Physical';
    this.contextTypeChanged.emit(contextType);
  }

  private scrollToAccordion(chipId: number): void {
    const element = document.getElementById(`accordion-${chipId}`);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'nearest' // or 'start' to scroll to top of element
      });
    }
  }

  public openIndividualContextDetail(item: any, type: string) {
    if (this.shouldCollapseExpandedItem(item, type)) {
      this.collapseItem();
      return;
    }
    this.expandedItem.set({ item, type });
    this.selectedContextItem.emit({ item, type });
    this.cdr.detectChanges();
    setTimeout(() => {
      this.scrollToCurrentlyExpandedCard();
    }, 200);
  }

  private shouldCollapseExpandedItem(item: any, type: string): boolean {
    const expanded = this.expandedItem();
    if (!expanded || expanded.type !== type) return false;

    // Special handling for Stakeholder items:
    // collapse when the currently clicked stakeholder represents the same logical stakeholder,
    // even if the instances differ (e.g. opened from map vs from sidebar list).
    if (type === 'Stakeholder') {
      const expandedUuid =
        expanded.item?.contextUuid || expanded.item?.contexts?.[0]?.uuid || null;
      const currentUuid =
        item?.contextUuid || item?.contexts?.[0]?.uuid || null;

      if (expandedUuid && currentUuid && expandedUuid === currentUuid) {
        return true;
      }

      // Fallback to deep equality if UUID-based match is not possible
      return isEqual(expanded.item, item);
    }

    // General case for non-stakeholder items: same item
    if (isEqual(expanded.item, item)) return true;

    return false;
  }

  private collapseItem(){
    this.onItemCollapse.emit();
    this.expandedItem.set(null);
  }

  onClickSingleItem(event: {item: any, type: string} | null){
    if (event) {
      this.selectedContextItem.emit({ item: event.item, type: event.type, isSingleItem: true });
    } else {
      this.selectedContextItem.emit({ item: null, type: null, isSingleItem: true });
    }
  }

  public scrollToCurrentlyExpandedCard(): void {
    if (!this.expandedItem()) return;
    const selector = '.detail-table-card1';
    const cards = document.querySelectorAll(selector);
    for (let i = 0; i < cards.length; i++) {
      const card = cards[i] as HTMLElement;
      const hasExpandedContent = card.querySelector('app-individual-context-detail') !== null;
      if (hasExpandedContent) {
        card.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
        card.classList.add('card-focused');
        setTimeout(() => card.classList.remove('card-focused'), 800);
        break;
      }
    }
  }

  onHoverStakeholder(stakeholder:any){
    this.onHoverContextItem('Stakeholders',stakeholder)
  }

  onHoverContextItem(type: string, item: any) {
    this.onHoverContext.emit({ type, item, is_subtype: true });
  }
  
  onLeaveContextItem() {
    this.onLeaveContext.emit();
  }

  closePanel() {
    this.onPanelClose.emit();
  }

  async addBulkContextItemsToProject(type: string): Promise<void> {
    if (this.createdContextTypes().has(type)) {
      return;
    }

    try 
    {
      this.isProcessing.set(true);
      this.processingType.set(type);
      const data = this.getContextDataByType(type);
      if (!data) {
        return;
      }

      const contextMap = new Map<string, ContextItemRelation>();
      const allStakeholders: any[] = [];

      if (type === 'Physical') {
        data.forEach((contextItem: any) => {
          const stakeholders = contextItem?.stakeholders || [];
          contextMap.set(contextItem.uuid, { contextItem, stakeholders });
          allStakeholders.push(...stakeholders);
        });
      } else {
        data?.context_subtypes?.forEach((sub: any) => {
          sub?.context_items?.forEach((contextItem: any) => {
            const stakeholders = contextItem?.parameters?.[0]?.stakeholders || [];
            contextMap.set(contextItem.uuid, { contextItem, stakeholders });
            allStakeholders.push(...stakeholders);
          });
        });
      }

      if (!contextMap.size) {
        console.warn('No context items found.');
        return;
      }

      const uniqueStakeholders = Array.from(
        new Map(
          allStakeholders.map(item => [
            this.getStakeholderKey(item),
            {
              name: item?.name || item?.text || item?.title,
              description: item?.description,
              locations: item?.locations || {},
              address: item?.address || ''
            }
          ])
        ).values()
      );


      // first create all stakeholders
      const createdStakeholders = uniqueStakeholders.length ? await this.createStakeholders(uniqueStakeholders) : [];
      
      const stakeholderLookup = new Map<string, any>();
      createdStakeholders.forEach(s => {
        stakeholderLookup.set(this.getStakeholderKey(s), s);
      });

      const contextsToCreate: any[] = [];
      contextMap.forEach(({ contextItem, stakeholders }) => {
        const newStakeholders = stakeholders.map(old => stakeholderLookup.get(this.getStakeholderKey(old))).filter(Boolean);
        const types = [this.contextTypes.find(ct => ct.name.toLowerCase() === type.toLowerCase())].filter(Boolean);

        const normalizedContext = {
          text: contextItem?.name || contextItem?.text,
          description: contextItem?.description,
          types,
          locations: {
            locatable_type: "context",
            polygons: contextItem?.geometry?.coordinates ? {
              coordinates: [contextItem?.geometry?.coordinates],
              type: 'MultiPolygon'
            }: null
          },
          source: contextItem?.source || 'app',
          metadata: contextItem,
          stakeholders: newStakeholders,
          uuid: contextItem?.uuid || null
        };

        contextsToCreate.push(normalizedContext);
      });

      // now create all context items with the created stakeholders
      await this.createContextItems(contextsToCreate);
      this.createdContextTypes.update(set => {
        const newSet = new Set(set);
        newSet.add(type);
        return newSet;
      });

      showFeedbackSaved();
    } 
    catch (error) 
    {
      showFeedbackError(error);
    } 
    finally 
    {
      this.isProcessing.set(false);
      this.processingType.set(null);
    }
  }

  async createStakeholders(stakeholders: any[]): Promise<any[]> {
    return await firstValueFrom(
      this._stakeholdersService.createBulkStakeholders(stakeholders)
    );
  }

  async createContextItems(contexts: any[]): Promise<any[]> {
    return await firstValueFrom(
      this._contextsService.createBulkContext(contexts)
    );
  }

  private getStakeholderKey(stakeholder: any): string {
    return `${stakeholder?.name ?? ''}_${stakeholder?.address ?? ''}`
      .toLowerCase()
      .trim();
  }

  onTabChange(event: any) {
    // Reset expanded item when switching tabs
    this.expandedItem.set(null);
    
    // When switching to Context tab (index 0), emit the currently active chip type
    // When switching to Stakeholders tab (index 1), we could handle it differently if needed
    if (event.index === 0) {
      // const contextType = this.contextChipsTypes.find(chip => chip.id === this.activeChipId)?.name || 'Physical';
      this.contextTypeChanged.emit('Physical');
    }

    if (event.index === 1) {
      // Transform physicalContextData into stakeholders array
      this.transformPhysicalContextToStakeholders();
      this.contextTypeChanged.emit('Stakeholders');
    }
  }

  private transformPhysicalContextToStakeholders(): void {
    this.transformedStakeholders = [];
    const stakeholdersMap = new Map<string, any>();

    // Check if physicalContextData is an array
    if (!Array.isArray(this.physicalContextData)) {
      return;
    }

    // Iterate through each physicalContextData item
    this.physicalContextData.forEach((physicalItem: any) => {
      // Extract stakeholders array from current item
      const stakeholders = physicalItem?.stakeholders || [];
      
      // Create context data without stakeholders array
      const { stakeholders: _, ...contextData } = physicalItem;

      // Process each stakeholder
      stakeholders.forEach((stakeholder: any) => {
        // Create a unique key for the stakeholder (using name, address, and physicalItem uuid)
        const stakeholderKey = stakeholder?.name 
          ? `${stakeholder.name}_${stakeholder.address || ''}_${physicalItem.uuid}` 
          : `${JSON.stringify(stakeholder)}_${physicalItem.uuid}`;

        if (stakeholdersMap.has(stakeholderKey)) {
          // Stakeholder already exists, add context to existing contexts array
          const existingStakeholder = stakeholdersMap.get(stakeholderKey);
          existingStakeholder.contexts.push(contextData);
        } else {
          // New stakeholder, create entry with contexts array and assign parent context uuid
          stakeholdersMap.set(stakeholderKey, {
            ...stakeholder,
            contextUuid: physicalItem.uuid,
            contexts: [contextData]
          });
        }
      });
    });

    // Convert map values to array
    this.transformedStakeholders = Array.from(stakeholdersMap.values());
  }

  onScroll(event: Event): void {
    // Clear previous timeout
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    // Set a timeout to detect which accordion is most visible
    this.scrollTimeout = setTimeout(() => {
      this.detectVisibleAccordion();
    }, 150); // Debounce scroll events
  }

  private detectVisibleAccordion(): void {
    const accordions = this.contextChipsTypes;
    let maxVisibility = 0;
    let mostVisibleChipId = this.activeChipId;

    accordions.forEach(chip => {
      const element = document.getElementById(`accordion-${chip.id}`);
      if (element) {
        const visibility = this.getElementVisibility(element);
        if (visibility > maxVisibility) {
          maxVisibility = visibility;
          mostVisibleChipId = chip.id;
        }
      }
    });

    // Only update if the most visible accordion has changed and has significant visibility
    if (mostVisibleChipId !== this.activeChipId && maxVisibility > 0.3) {
      this.activeChipId = mostVisibleChipId;
      const contextType = this.contextChipsTypes.find(chip => chip.id === mostVisibleChipId)?.name || 'Physical';
      this.contextTypeChanged.emit(contextType);
    }
  }

  private getElementVisibility(element: HTMLElement): number {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    
    // Calculate what portion of the element is visible
    const visibleTop = Math.max(0, rect.top);
    const visibleBottom = Math.min(viewportHeight, rect.bottom);
    const visibleHeight = Math.max(0, visibleBottom - visibleTop);
    const elementHeight = rect.height;
    
    return elementHeight > 0 ? visibleHeight / elementHeight : 0;
  }

  private normalizeContextType(type: string): string {
    const normalized = (type || '').toLowerCase().trim();
    return normalized === 'societal' ? 'social' : normalized;
  }

  public getContextDataByType(type: string): any {
    if (type === 'Physical') {
      return this.physicalContextData;
    }

    if (!Array.isArray(this.ContextanalysisData)) {
      return null;
    }

    const targetType = this.normalizeContextType(type);
    return this.ContextanalysisData.find((context: any) => {
      const contextType = this.normalizeContextType(context?.context_type || '');
      return contextType === targetType;
    }) ?? null;
  }

  calculateContextItems(type: string): number {
    const data = this.getContextDataByType(type);
    if (type === 'Physical') {
      return data?.length || 0;
    }

    const subtypesCount =
      data?.context_subtypes?.reduce((total, item) => {
        return total + (item?.context_items?.length || 0);
      }, 0) || 0;

    const rootItemsCount = data?.context_items?.length || 0;

    return subtypesCount + rootItemsCount;
  }

  public areBothEqual(item: any, uuid: string): boolean {
    const ctxs = item?.contexts;
    if (item?.id && item?.created_at) {
      return item?.contextUuid === uuid;
    }
    return ctxs?.[0]?.uuid === uuid;
  }

  getContextLabel(type: string, count: number): string {
    const isSingular = count <= 1;
    switch (type) {
      case 'Regulatory':
        return isSingular ? 'contexts.plan' : 'contexts.plans';
      case 'Political':
        return isSingular ? 'contexts.municipality' : 'contexts.municipalities';
      default:
        return isSingular ? 'contexts.neighborhood' : 'contexts.neighborhoods';
    }
  }

}
